import { motion } from 'framer-motion'
import { ShieldCheck } from 'lucide-react'
import { resolveSite } from '@/lib/site'
import type { SlideProps } from '@/components/SlideWizard'
import { useSlideCopy } from '@/lib/i18n'
import { copy } from './Slide01EntryHook.copy'

const ease = [0.16, 1, 0.3, 1] as const
const fadeUp = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } }

export default function Slide01EntryHook(_props: SlideProps) {
  const c = useSlideCopy(copy)

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 mx-auto">
      <motion.div {...fadeUp} transition={{ duration: 0.5, ease }}>
        {/* The GranGuard face opens with the G, as the real app does; the
            partner faces keep the neutral shield. */}
        {resolveSite() === 'granguard' ? (
          <div className="mb-6 h-20 w-20">
            <img src="/g_logo_violet.png" alt={c.logoAlt} className="h-full w-full object-contain" />
          </div>
        ) : (
          <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-violet-50 text-violet-600">
            <ShieldCheck className="h-10 w-10" strokeWidth={1.75} />
          </div>
        )}
      </motion.div>

      <motion.p
        {...fadeUp}
        transition={{ duration: 0.5, delay: 0.1, ease }}
        className="mb-4 text-sm sm:text-base uppercase tracking-[0.25em] text-slate-400 font-semibold"
      >
        {c.eyebrow}
      </motion.p>

      <motion.h1
        {...fadeUp}
        transition={{ duration: 0.6, delay: 0.2, ease }}
        className="text-5xl sm:text-7xl font-semibold text-slate-900 tracking-tight whitespace-pre-line leading-[1.1]"
      >
        {c.title}
      </motion.h1>

      <motion.p
        {...fadeUp}
        transition={{ duration: 0.6, delay: 0.4, ease }}
        className="mt-6 text-xl sm:text-2xl text-slate-500 leading-relaxed max-w-lg mx-auto"
      >
        {c.subtitle}
      </motion.p>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.2, ease }}
        className="mt-10 flex items-center gap-2 text-xs sm:text-sm text-slate-400 normal-case tracking-normal font-medium select-none"
      >
        <span className="hidden sm:inline">{c.keyboardTip}</span>
        <span className="sm:hidden">{c.touchTip}</span>
      </motion.p>
    </div>
  )
}
